import React from 'react';
import classNames from 'classnames';

import Field from '../../Field';
import { MARKETPLACE_INTRODUCTION_ID } from '../../../../extensions/LandingPage/config/constants';

import css from './SectionContainer.module.css';

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * This component can be used to wrap some common styles and features of Section-level components.
 * E.g: const SectionHero = props => (<SectionContainer><H1>Hello World!</H1></SectionContainer>);
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.id id of the section
 * @param {string?} props.as tag/element name. Defaults to 'section'.
 * @param {ReactNode} props.children
 * @param {Object} props.appearance
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} containing wrapper that can be used inside Block components.
 */
const SectionContainer = props => {
  const {
    className,
    rootClassName,
    id,
    as,
    children,
    appearance,
    options,
    contentClassName,
    ...otherProps
  } = props;
  const Tag = as || 'section';
  const classes = classNames(rootClassName || css.root, className);

  return (
    <Tag className={classes} id={id} {...otherProps}>
      {appearance?.fieldType === 'customAppearance' ? (
        id === MARKETPLACE_INTRODUCTION_ID ? (
          <picture className={css.heroBanner}>
            <source srcSet="/images/homepage-hero-mobile.webp" media="(max-width: 768px)" type="image/webp" />
            <source srcSet="/images/homepage-hero-mobile.jpg" media="(max-width: 768px)" type="image/jpeg" />
            <source srcSet="/images/homepage-hero.webp" type="image/webp" />
            <img src="/images/homepage-hero.jpg" alt="" className={css.heroBannerImg} />
          </picture>
        ) : (
          <Field
            data={{ alt: `Background image for ${id}`, ...appearance }}
            className={className}
            options={options}
          />
        )
      ) : null}

      <div className={classNames(css.sectionContent, contentClassName)}>{children}</div>
    </Tag>
  );
};

export default SectionContainer;
